<template>
  <Message v-if="error && !rows.length" severity="error" :closable="false" data-testid="student-fields-error">
    <div class="flex flex-wrap items-center justify-between gap-3 w-full">
      <span>Could not load the student fields.</span>
      <Button label="Try again" icon="pi pi-refresh" size="small" severity="secondary" @click="refetch().catch(() => {})" />
    </div>
  </Message>

  <DataTable v-else :value="rows" :loading="loading && !rows.length" size="small" dataKey="key" scrollable
    data-testid="custom-fields-table">
    <Column header="Order" style="width: 6rem">
      <template #body="{ data }">
        <div v-if="data.field" class="flex">
          <Button icon="pi pi-arrow-up" text rounded size="small" aria-label="Move up"
            :disabled="!canUpdate || customIndex(data) === 0 || reordering" @click="$emit('move', data.field.id, -1)"
            :data-testid="`btn-custom-field-up-${data.field.key}`" />
          <Button icon="pi pi-arrow-down" text rounded size="small" aria-label="Move down"
            :disabled="!canUpdate || customIndex(data) === customCount - 1 || reordering" @click="$emit('move', data.field.id, 1)"
            :data-testid="`btn-custom-field-down-${data.field.key}`" />
        </div>
      </template>
    </Column>
    <Column header="Field">
      <template #body="{ data }">
        <div class="flex flex-col items-start gap-0.5" :data-testid="`student-field-${data.key}`">
          <span class="font-medium">{{ data.label }}</span>
          <code v-if="data.field" class="text-xs text-surface-500">{{ data.field.key }}</code>
          <span v-else class="text-xs text-surface-500">Built-in</span>
          <Tag v-if="!data.active" value="Retired" severity="secondary" />
        </div>
      </template>
    </Column>
    <Column header="Type">
      <template #body="{ data }">
        <span class="text-sm" :data-testid="`student-field-kind-${data.key}`">{{ data.kind }}</span>
      </template>
    </Column>
    <Column header="Required">
      <template #body="{ data }">
        <Checkbox :modelValue="data.required" binary :inputId="`required-${data.key}`"
          :disabled="!canUpdate || !canToggleRequired(data) || savingKey !== null"
          :aria-label="`${data.label}: required`"
          @update:modelValue="(on: boolean) => run(data, () => setRequired(data, on), on ? 'required' : 'not required')"
          :data-testid="`student-field-required-${data.key}`" />
      </template>
    </Column>
    <Column header="Students">
      <template #body="{ data }">
        <SelectButton :modelValue="data.access" :options="studentAccessChoices(data)" optionLabel="label" optionValue="value"
          :allowEmpty="false" size="small" class="whitespace-nowrap" :disabled="!canUpdate || !data.active || savingKey !== null"
          :aria-label="`${data.label}: what students can do`"
          @update:modelValue="(access: CustomFieldStudentAccess) => run(data, () => setAccess(data, access), accessLabel(access))"
          :data-testid="`student-field-access-${data.key}`" />
        <small v-if="rowErrors[data.key]" class="block text-xs text-red-500 max-w-64 mt-1"
          :data-testid="`student-field-error-${data.key}`">{{ rowErrors[data.key] }}</small>
        <small v-else-if="data.note" class="block text-xs text-surface-500 max-w-64 mt-1"
          :data-testid="`custom-field-student-access-note-${data.field?.key ?? data.key}`">{{ data.note }}</small>
      </template>
    </Column>
    <Column header="Students with a value">
      <template #body="{ data }">
        <span :data-testid="`student-field-count-${data.key}`">{{ data.valuesCount }}</span>
      </template>
    </Column>
    <Column header="" style="width: 14rem">
      <template #body="{ data }">
        <div class="flex items-center justify-end">
          <Button v-if="canSend && askable(data)" label="Ask students" icon="pi pi-send" text size="small" class="whitespace-nowrap"
            @click="$emit('ask', data.key)" :data-testid="`btn-ask-students-${data.key}`" />
          <template v-if="data.field">
            <Button v-if="canUpdate" icon="pi pi-pencil" text rounded size="small" aria-label="Edit"
              @click="$emit('edit', data.field)" :data-testid="`btn-custom-field-edit-${data.field.key}`" />
            <Button v-if="canDelete" icon="pi pi-trash" severity="danger" text rounded size="small" aria-label="Delete"
              @click="$emit('delete', data.field)" :data-testid="`btn-custom-field-delete-${data.field.key}`" />
          </template>
        </div>
      </template>
    </Column>
  </DataTable>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { CustomFieldStudentAccess } from '@/types/composition-functions';
import type { CustomFieldFieldsFragment } from '@/types/operations';
import { useStudentFields } from '@/composables/useStudentFields';
import { canToggleRequired, studentAccessChoices, type StudentFieldRow } from '@/utils/studentFields';
import { STUDENT_ACCESS_OPTIONS } from '@/utils/studentProfileForm';
import { firstValidationMessage } from '@/utils/error';
import { extractGraphQLError } from '@/utils/graphqlError';

/**
 * Settings → Custom Fields → Students → Fields: the school's own fields and
 * the built-in ones in one table. Required and the "Students" setting save
 * as they change; "Ask students" starts a data request for a field students
 * can fill in.
 */
defineProps<{ canUpdate: boolean; canDelete: boolean; canSend: boolean; reordering: boolean }>();
defineEmits<{
  move: [id: string, direction: -1 | 1];
  edit: [field: CustomFieldFieldsFragment];
  delete: [field: CustomFieldFieldsFragment];
  ask: [key: string];
}>();

const toast = useToast();
const { rows, loading, error, refetch, savingKey, setAccess, setRequired } = useStudentFields();

const customRows = computed(() => rows.value.filter(r => r.field));
const customCount = computed(() => customRows.value.length);
const customIndex = (row: StudentFieldRow) => customRows.value.findIndex(r => r.key === row.key);

/** Students can be asked for it now: open to them, and not held read-only by its state or LGA. */
const askable = (row: StudentFieldRow) => row.active && row.access === CustomFieldStudentAccess.Edit && !row.note;

const accessLabel = (access: CustomFieldStudentAccess) => STUDENT_ACCESS_OPTIONS.find(o => o.value === access)?.label ?? '';

/** What the server refused, under the row it refused it for. */
const rowErrors = ref<Record<string, string>>({});

async function run(row: StudentFieldRow, work: () => Promise<unknown>, done: string) {
  delete rowErrors.value[row.key];
  try {
    await work();
    toast.add({ severity: 'success', summary: 'Saved', detail: `${row.label}: ${done}.`, life: 3000 });
  } catch (err) {
    const refused = firstValidationMessage(err);
    if (refused) {
      rowErrors.value[row.key] = refused;
      return;
    }
    console.error(err);
    toast.add({ severity: 'error', summary: `Could not save ${row.label}`, detail: extractGraphQLError(err, 'Please try again.'), life: 5000 });
  }
}
</script>
